import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Link as MuiLink,
  TextField,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useHistory } from "react-router-dom";
import { actions, selectors } from "rdx";
import { useUpdateEnrollmentMutation } from "rdx/modules/enrollment/endpoints";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import SessionClient from "util/SessionClient";
import * as S from "./styles";

const CARD_SAVED_MESSAGE = "Your card was saved. We'll finish processing your payment shortly.";
const CARD_DECLINED_MESSAGE =
  "We were unable to process your payment. Please check your payment details or try a different payment method.";
const GENERIC_ERROR_MESSAGE = "We couldn't update your payment method. Please try again.";
const INVALID_CARD_MESSAGE = "Invalid card information.";
const REFRESH_FAILED_MESSAGE =
  "Your payment went through, but we couldn't refresh your account. Please reload this page to continue.";

const stripeElementStyle = {
  base: {
    fontSize: "16px",
    fontFamily: "'Barlow', sans-serif",
    color: "rgba(255,255,255,0.9)",
    letterSpacing: "0.025em",
    "::placeholder": {
      color: "rgba(255,255,255,0.35)",
    },
  },
  invalid: {
    color: "#f87171",
  },
};

type PlanServiceT = {
  name?: string | null;
  monthly?: number | string | null;
};

type CardCompleteT = {
  number: boolean;
  expiry: boolean;
  cvc: boolean;
};

type FormMessageT = {
  severity: "error" | "info" | "warning";
  text: string;
};

type ApiErrorDetailT = {
  field?: string;
  message?: string;
};

type ApiErrorT = {
  status?: number | string;
  data?: { error?: ApiErrorDetailT };
  error?: { status?: number | string; data?: { error?: ApiErrorDetailT } };
};

const formatMonthly = (monthly: PlanServiceT["monthly"]) => {
  const amount = Number(monthly);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
};

const buildSubText = (service?: PlanServiceT | null) => {
  const name = service?.name?.trim();
  if (!name) {
    return "We couldn't process the payment for your subscription. Add a card below to keep your account active.";
  }
  const price = formatMonthly(service?.monthly);
  const priceText = price ? ` (${price}/mo)` : "";
  return `We couldn't process the payment for your ${name} subscription${priceText}. Add a card below to keep your account active.`;
};

const parseApiError = (err: unknown) => {
  const apiError = err as ApiErrorT | null | undefined;
  return {
    status: apiError?.status ?? apiError?.error?.status,
    detail: apiError?.data?.error ?? apiError?.error?.data?.error,
  };
};

const PaymentRequiredForm = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const stripe = useStripe();
  const elements = useElements();
  const [updateEnrollment] = useUpdateEnrollmentMutation();
  const currentUser = useAppSelector(selectors.getCurrentUser);

  const [cardholderName, setCardholderName] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const [cardComplete, setCardComplete] = useState<CardCompleteT>({ number: false, expiry: false, cvc: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [message, setMessage] = useState<FormMessageT | null>(null);

  const subText = useMemo(() => {
    const storeService: PlanServiceT | undefined = currentUser?.enrollment?.service;
    const service = storeService?.name ? storeService : new SessionClient().user?.props?.enrollment?.service;
    return buildSubText(service);
  }, [currentUser]);

  const isFormValid =
    cardholderName.trim().length > 0 && tosAccepted && cardComplete.number && cardComplete.expiry && cardComplete.cvc;

  const refreshSession = async () => {
    try {
      await new SessionClient().user?.reload("payment-required");
      return true;
    } catch (_e) {
      return false;
    }
  };

  const handleUpdated = async (paymentRequired: boolean | undefined) => {
    const refreshed = await refreshSession();

    if (paymentRequired === true) {
      setMessage({ severity: "info", text: CARD_SAVED_MESSAGE });
      return;
    }

    if (!refreshed) {
      setMessage({ severity: "warning", text: REFRESH_FAILED_MESSAGE });
      return;
    }

    if (new SessionClient().user?.props?.enrollment?.paymentRequired) {
      setMessage({ severity: "info", text: CARD_SAVED_MESSAGE });
      return;
    }

    history.push("/dashboard");
  };

  const handleRejected = (err: unknown) => {
    const { status, detail } = parseApiError(err);

    if (status === 403) {
      history.replace("/verify-email-required");
      return;
    }

    if (status === 400 && detail?.field === "stripe") {
      setMessage({ severity: "error", text: CARD_DECLINED_MESSAGE });
      return;
    }

    const serverMessage = status === 400 && typeof detail?.message === "string" ? detail.message : "";
    setMessage({ severity: "error", text: serverMessage || GENERIC_ERROR_MESSAGE });
  };

  const handleSubmit = async () => {
    if (!stripe || !elements || !isFormValid || isSubmitting) return;

    setStripeError(null);
    setMessage(null);
    setIsSubmitting(true);

    try {
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) return;

      const { error, token } = await stripe.createToken(cardElement, { name: cardholderName.trim() });

      if (error || !token) {
        setStripeError(error?.message || INVALID_CARD_MESSAGE);
        return;
      }

      try {
        const data = await updateEnrollment({ tokenId: token.id, tos: true }).unwrap();
        await handleUpdated(data.enrollment?.paymentRequired);
      } catch (err) {
        handleRejected(err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    dispatch(actions.logout());
    const session = new SessionClient();
    if (session.user) {
      session.user.clearStore();
    }
    session.clear();
    history.push("/login");
  };

  return (
    <Box sx={S.formCard}>
      <Typography component="h1" sx={S.headingText}>
        Update your payment method
      </Typography>
      <Typography sx={S.subText}>{subText}</Typography>

      {message && (
        <Alert severity={message.severity} sx={S.alert}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Cardholder name"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            sx={S.darkInput}
            disabled={isSubmitting}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography sx={S.stripeFieldLabel}>Card number</Typography>
          <Box sx={S.stripeElementWrapper}>
            <CardNumberElement
              options={{ style: stripeElementStyle, showIcon: true }}
              onChange={(e) => setCardComplete((prev) => ({ ...prev, number: e.complete }))}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={S.stripeFieldLabel}>Expiration</Typography>
          <Box sx={S.stripeElementWrapper}>
            <CardExpiryElement
              options={{ style: stripeElementStyle }}
              onChange={(e) => setCardComplete((prev) => ({ ...prev, expiry: e.complete }))}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={S.stripeFieldLabel}>CVC</Typography>
          <Box sx={S.stripeElementWrapper}>
            <CardCvcElement
              options={{ style: stripeElementStyle }}
              onChange={(e) => setCardComplete((prev) => ({ ...prev, cvc: e.complete }))}
            />
          </Box>
        </Grid>

        {stripeError && (
          <Grid item xs={12}>
            <Typography sx={S.stripeErrorText}>{stripeError}</Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={tosAccepted}
                onChange={(e) => setTosAccepted(e.target.checked)}
                disabled={isSubmitting}
                sx={S.checkbox}
              />
            }
            label={
              <Typography sx={S.tosLabel}>
                I agree to the{" "}
                <MuiLink href="https://powur.com/terms" target="_blank" rel="noopener noreferrer" sx={S.tosLink}>
                  Terms of Service
                </MuiLink>
              </Typography>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Typography sx={S.paymentDisclaimer}>
            <LockIcon sx={S.lockIcon} />
            Payments are encrypted and processed securely via Stripe.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            sx={S.ctaButton}
            disabled={isSubmitting || !isFormValid}
            data-testid="payment-required-submit"
          >
            {isSubmitting ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Pay now"}
          </Button>
        </Grid>

        <Grid item xs={12} sx={{ textAlign: "center" }}>
          <Button onClick={handleLogout} sx={S.footerLink}>
            Log out
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PaymentRequiredForm;
